/**
 * Storage for the transient Akoya OAuth flow parameters.
 *
 * These values are kept in sessionStorage rather than localStorage:
 *   - sessionStorage is scoped to a single tab and is discarded when that tab
 *     closes, so nothing survives on disk after the browser is closed and
 *     nothing is shared with other tabs or other users of the machine.
 *   - The authorization flow leaves the SPA entirely (full-page redirect to the
 *     Akoya IdP and back to /callback), so purely in-memory state cannot be
 *     used here. sessionStorage is preserved across that round trip because the
 *     tab returns to the same origin.
 *
 * Keys are namespaced to keep them from colliding with unrelated application
 * state (notably the JWT verification key App.js keeps under 'key').
 *
 * Values are removed as soon as the flow reaches a terminal state - see
 * clearFlow() - so the window in which they exist is the flow itself.
 */

const NAMESPACE = 'akoya.';

// Every key this flow is allowed to persist. Anything not listed here - most
// importantly the OAuth client secret - must never reach browser storage.
export const FLOW_KEYS = Object.freeze([
  'apiurl',
  'key',
  'clientid',
  'akoyaauthurl',
  'callback',
  'connector',
  'responsetype',
  'scope',
  'state',
  'request-token',
]);

// sessionStorage access throws in some privacy modes; degrade instead of
// breaking the page.
function store() {
  try {
    return window.sessionStorage;
  } catch (e) {
    return null;
  }
}

export function setFlowValue(name, value) {
  if (!FLOW_KEYS.includes(name)) {
    throw new Error(`Refusing to persist unexpected Akoya flow key: ${name}`);
  }
  // Absent query parameters arrive as null; persisting the string "null"
  // silently corrupts the authorization URL built from these values.
  if (value === null || value === undefined) {
    return;
  }
  const s = store();
  if (s) {
    s.setItem(NAMESPACE + name, value);
  }
}

export function getFlowValue(name) {
  const s = store();
  return s ? s.getItem(NAMESPACE + name) : null;
}

/** Remove every value this flow persisted. */
export function clearFlow() {
  const s = store();
  if (!s) {
    return;
  }
  FLOW_KEYS.forEach((name) => s.removeItem(NAMESPACE + name));
}
